import React, { useMemo } from 'react';
import { cn } from './cn.js';
import { toCapitalizedCase } from '../lib/format.js';

interface HighlightedMatchProps {
  readonly target: string;
  readonly reference: string;
  readonly className?: string;
}

function normalizeWord(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * Visual diff helper for operators:
 * - Automatically displays candidate names in Capitalized Case (Title Case).
 * - Matched tokens between target and reference are highlighted in vibrant green (text-herb-700 font-bold).
 * - Unmatched tokens / variations are highlighted in red warning badges (text-rust-700 bg-rust-100).
 */
export function HighlightedMatch({ target, reference, className }: HighlightedMatchProps): React.ReactElement {
  const segments = useMemo(() => {
    if (!target) return [];
    const formattedTarget = toCapitalizedCase(target);
    if (!reference) return [{ text: formattedTarget, isMatch: false }];

    const refWords = reference.match(/[\p{L}\p{N}]+/gu) ?? [];
    const refTokens = new Set<string>();

    for (const raw of refWords) {
      const norm = normalizeWord(raw);
      if (norm) {
        refTokens.add(norm);
        const sizeMatch = /^(\d+)([a-z]+)$/.exec(norm);
        const sizeNum = sizeMatch?.[1];
        const sizeUnit = sizeMatch?.[2];
        if (sizeNum !== undefined && sizeUnit !== undefined) {
          refTokens.add(sizeNum);
          refTokens.add(sizeUnit);
        }
      }
    }

    const tokens = formattedTarget.split(/([^\p{L}\p{N}]+)/u);
    type Chunk = { text: string; isWord: boolean; isMatch: boolean };
    const chunks: Chunk[] = [];

    for (const token of tokens) {
      if (!token) continue;
      const isWord = /[\p{L}\p{N}]/u.test(token);
      if (isWord) {
        const norm = normalizeWord(token);
        let isMatch = refTokens.has(norm);
        if (!isMatch) {
          const sizeMatch = /^(\d+)([a-z]+)$/.exec(norm);
          const sizeNum = sizeMatch?.[1];
          const sizeUnit = sizeMatch?.[2];
          if (
            sizeNum !== undefined &&
            sizeUnit !== undefined &&
            refTokens.has(sizeNum) &&
            refTokens.has(sizeUnit)
          ) {
            isMatch = true;
          }
        }
        chunks.push({ text: token, isWord: true, isMatch });
      } else {
        chunks.push({ text: token, isWord: false, isMatch: false });
      }
    }

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      if (chunk === undefined || chunk.isWord) continue;
      const prev = chunks[i - 1];
      const next = chunks[i + 1];
      // Attach whitespace and punctuation to matching text if either side matches
      if (prev?.isMatch === true || next?.isMatch === true) {
        chunk.isMatch = true;
      } else {
        chunk.isMatch = false;
      }
    }

    const merged: { text: string; isMatch: boolean }[] = [];
    for (const c of chunks) {
      const last = merged[merged.length - 1];
      if (last !== undefined && last.isMatch === c.isMatch) {
        last.text += c.text;
      } else {
        merged.push({ text: c.text, isMatch: c.isMatch });
      }
    }

    return merged;
  }, [target, reference]);

  return (
    <span className={cn('inline', className)}>
      {segments.map((seg, idx) =>
        seg.isMatch ? (
          <span key={idx} className="text-herb-700 font-bold">
            {seg.text}
          </span>
        ) : (
          <span
            key={idx}
            className="text-rust-700 bg-rust-100 font-bold px-1.5 py-0.5 rounded border border-rust-600/30 mx-0.5 inline-block align-baseline shadow-2xs"
          >
            {seg.text}
          </span>
        ),
      )}
    </span>
  );
}
